import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { partnerOffersQueryKey } from "@/features/reservations/hooks/useReservationAnswers";
import {
  partnerOffersRepo,
  type OfferActivityOption,
} from "@/data/repos/partnerOffersRepo";
import type { OfferDraft } from "@/features/offers/hooks/useOfferForm";
import { slotMoveRefusalMessage } from "@/features/offers/sales-locks";
import { PortalRefusal } from "@/data/api/client";
import { slotHourMoved } from "@/domain/offer/rules";
import type { PartnerOffer } from "@/domain/offer/types";
import { observability } from "@/lib/observability";

export type { OfferActivityOption };

/** A slot nobody published on is invisible in the portal and blocks its hour
 *  forever, so a publish that fails takes the slots it made with it. The
 *  cleanup may fail too — and it must not replace the answer the partner is
 *  owed, which is why it is reported rather than thrown. */
async function discardSlots(slotIds: number[]): Promise<void> {
  await Promise.all(
    slotIds.map((slotId) =>
      partnerOffersRepo.removeSlot(slotId).catch((cleanupFailure: unknown) => {
        observability.captureError(cleanupFailure, { slotId });
      }),
    ),
  );
}

async function publishOffer(draft: OfferDraft): Promise<void> {
  const slotIds = await partnerOffersRepo.createSlot({
    activityId: draft.activityId,
    startTime: draft.startTime,
    endTime: draft.endTime,
    capacity: draft.capacity,
  });
  try {
    await partnerOffersRepo.publish({
      slotIds,
      priceDiscounted: draft.priceDiscounted,
      saleWindow: draft.saleWindow,
      notes: null,
      // A publish has no stored offer behind it, so the draft is never in the
      // mode that omits the questions — the fallback only satisfies the type.
      customFields: draft.customFields ?? null,
    });
  } catch (publishFailure) {
    await discardSlots(slotIds);
    throw publishFailure;
  }
}

export interface SeriesPublication {
  published: number;
  /** The drafts nobody took, so a retry asks for exactly those — resubmitting
   *  the whole preview would collide with what already went through. */
  failed: OfferDraft[];
}

/** The generator publishes N independent termíny, so one refused publish must
 *  not take the rest with it — what got through is what the partner is told,
 *  and the rest are theirs to try again. */
async function publishOffers(drafts: OfferDraft[]): Promise<SeriesPublication> {
  let published = 0;
  const failed: OfferDraft[] = [];
  for (const draft of drafts) {
    try {
      await publishOffer(draft);
      published += 1;
    } catch (publishFailure) {
      observability.captureError(publishFailure, { startTime: draft.startTime.toISOString() });
      failed.push(draft);
    }
  }
  return { published, failed };
}

export interface OfferEdit {
  offer: PartnerOffer;
  draft: OfferDraft;
}

/** The discount overlay is the offer's own save and always travels; the hour
 *  belongs to the slot and follows only when the partner moved it. The two are
 *  not one transaction — the offer is saved first, so a refused move leaves the
 *  price and the selling time stored, which is what the partner is told. */
async function saveOffer({ offer, draft }: OfferEdit): Promise<void> {
  await partnerOffersRepo.updateOffer(offer.id, {
    priceDiscounted: draft.priceDiscounted,
    saleWindow: draft.saleWindow,
    customFields: draft.customFields,
  });
  // Capacity lives on the slot like the hour, and is refused by the API below
  // the seats already taken — so it travels only when the partner changed it,
  // and its refusal is the partner's to read rather than a silent no-op.
  if (draft.capacity !== offer.capacity) {
    await partnerOffersRepo.setCapacity(offer.slotId, draft.capacity);
  }
  if (!slotHourMoved(offer, draft)) return;
  try {
    await partnerOffersRepo.moveSlot(offer.slotId, {
      startTime: draft.startTime,
      endTime: draft.endTime,
    });
  } catch (moveFailure) {
    throw new PortalRefusal(slotMoveRefusalMessage(moveFailure));
  }
}

/** Authoring goes through `lastgo-api`, which owns the slot and the sleva laid
 *  over it, so the screen reads and writes one representation. */
export function useOffers() {
  const queryClient = useQueryClient();

  const activitiesQuery = useQuery<OfferActivityOption[]>({
    queryKey: ["offer-activity-options"],
    queryFn: () => partnerOffersRepo.listActivityOptions(),
  });

  const refreshOffers = () => {
    void queryClient.invalidateQueries({ queryKey: partnerOffersQueryKey });
  };

  const createOffer = useMutation({
    mutationFn: publishOffer,
    onSettled: refreshOffers,
  });

  const createOffers = useMutation({
    mutationFn: publishOffers,
    onSettled: refreshOffers,
  });

  const updateOffer = useMutation({
    mutationFn: saveOffer,
    onSettled: refreshOffers,
  });

  return {
    activities: activitiesQuery.data ?? [],
    createOffer,
    createOffers,
    updateOffer,
  };
}
